// MainPage.js - the workspace shell (from logistics-workflow): header, the tab bar (a menu on phones), the demo banner,
// and the active tab (pages/StudioWorkspace.js). It loads the signed-in person's profile for everything inside, and starts
// the welcome walkthrough on their first visit.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useMobileContext } from "../context/MobileContext";
import { ProfileContext, isOwner } from "../context/ProfileContext";
import { StudioTourProvider, useStudioTours } from "../reacttour/StudioTourContext";
import { getProfile } from "../api/studioAPI";
import { listPlans } from "../game-builder/api/gameBuilderAPI";
import analytics from "../services/analytics";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DemoOrgBanner from "../components/DemoOrgBanner";
import LoadingOverlay from "../components/common/LoadingOverlay";
import { Tabs, TabsList } from "../components/common/Tabs";
import StudioWorkspace from "./StudioWorkspace";
import { TABS } from "./tabs";
import styles from "../styles";
import "../responsive.css";

const TAB_KEYS = TABS.map((t) => t.key);
const labelOf = (key) => TABS.find((t) => t.key === key)?.label || "Dashboard";

function WelcomeTour() {
  const { profile } = React.useContext(ProfileContext);
  const { autoStart } = useStudioTours();
  useEffect(() => {
    if (!profile || (profile.tours_done || []).includes("welcome")) return undefined;
    let live = true;
    const timer = setTimeout(async () => {
      const hasPlans = await listPlans().then((d) => d.plans.length > 0).catch(() => false);
      if (live) autoStart("welcome", { hasPlans, isDemo: !!profile.org?.is_demo, isOwner: isOwner(profile) });
    }, 900);                                                      // give the page time to draw first
    return () => { live = false; clearTimeout(timer); };
  }, [profile, autoStart]);
  return null;
}

const MainPage = ({ token, logout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobileContext();
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const segment = location.pathname.replace(/^\/app\/?/, "").split("/")[0];
  const activeTab = TAB_KEYS.includes(segment) ? segment : "dashboard";

  const refresh = useCallback(() => {
    getProfile().then((p) => { setProfile(p); setProfileError(""); }).catch((e) => setProfileError(e.message));
  }, []);
  useEffect(() => { refresh(); }, [refresh, token]);

  const setToursDone = useCallback((update) => {
    setProfile((p) => (p ? { ...p, tours_done: typeof update === "function" ? update(p.tours_done) : update } : p));
  }, []);

  useEffect(() => {
    analytics.pageView(`/app/${activeTab}`, `SimulateJob - ${labelOf(activeTab)}`);
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setMobileMenuOpen(false);
    navigate(`/app/${tab}`);
  };

  if (!profile && !profileError) return <LoadingOverlay message="Loading your workspace..." />;

  return (
    <ProfileContext.Provider value={{ profile, refresh, setToursDone }}>
      <StudioTourProvider>
        <WelcomeTour />
        <div style={styles.container}>
          <Header logout={logout} />
          <div className="main-layout">
            <main className="main-content">
              {profileError && <p role="alert" style={{ color: "#b91c1c" }}>{profileError}</p>}
              <DemoOrgBanner logout={logout} />
              {isMobile ? (
                <div style={{ marginBottom: 20 }}>
                  <button
                    id="mobile-menu-toggle"
                    type="button"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", border: "1px solid #ddd",
                      borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: "1rem", fontWeight: 600 }}
                  >
                    {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    {labelOf(activeTab)}
                  </button>
                  {mobileMenuOpen && (
                    <div style={{ marginTop: 8, border: "1px solid #ddd", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                      {TABS.map((t) => (
                        <button
                          key={t.key}
                          id={`mobile-${t.key}`}
                          type="button"
                          onClick={() => handleTabChange(t.key)}
                          style={{ width: "100%", padding: "12px 16px", border: "none", borderBottom: "1px solid #f0f0f0",
                            background: t.key === activeTab ? "#f0f8ff" : "#fff", textAlign: "left", cursor: "pointer",
                            fontSize: "0.95rem", fontWeight: t.key === activeTab ? 600 : 500,
                            color: t.key === activeTab ? "#0066cc" : "#333" }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <TabsList id="app-tabs">
                    {TABS.map((t) => (
                      <button id={t.key} key={t.key} data-tab={t.key} type="button">{t.label}</button>
                    ))}
                  </TabsList>
                </Tabs>
              )}
              <StudioWorkspace activeTab={activeTab} token={token} logout={logout} />
            </main>
          </div>
          <Footer />
        </div>
      </StudioTourProvider>
    </ProfileContext.Provider>
  );
};

export default MainPage;
